import { useEffect, useState } from 'react';
import { message, Button, notification, Modal } from 'antd';
import { GiftOutlined, TrophyOutlined } from '@ant-design/icons';
import { motion } from 'motion/react';
import ConfettiExplosion from 'react-confetti-explosion';
import MyrewardsPresentation from '../../presentation/myrewardsPresentation';
import { useRnRCommonService } from '../../../RnRCommonComponent/service/RnRCommon.service.jsx';
import useRewardDetails from '../../../RnRCommonComponent/RewardList/hooks/useRewardDetails';
import { useSelector } from 'react-redux';
import { snappy } from 'src/_shared';
import { useApolloClient } from '@apollo/client';
import SnappyGiftService from '../../service/SnappyGift.service.jsx';
import GiftImage from 'src/_shared/assets/snappy_present.png';

const confettiConfig = {
	force: 0.9,
	duration: 3000,
	particleCount: 300,
	width: '2000',
	height: 1600,
	colors: ['#f650a9', '#00c3ff', '#fffc00', '#00ff87', '#ff0055', '#7700ff']
};

const MyrewardsContainer = () => {
	const [campaigns, setCampaigns] = useState([]);
	const currentUser = useSelector((state) => state.user.currentUser);
	const [isViewRewardModalVisible, setIsViewRewardModalVisible] =
		useState(false);
	const [testRecognition, setTestRecognition] = useState(null);
	const [disableSpotlight, setDisableSpotlight] = useState(false);
	const [recentActivities, setRecentActivities] = useState([]);
	const [isLoadingActivities, setIsLoadingActivities] = useState(true);
	const [badgeAssignments, setBadgeAssignments] = useState([]);
	const [snappyGifts, setSnappyGifts] = useState([]);
	const [badgeGiftsWithStatus, setBadgeGiftsWithStatus] = useState([]);
	const [isViewBadgeModalVisible, setIsViewBadgeModalVisible] = useState(false);
	
	const [showGiftModal, setShowGiftModal] = useState(false);
	const [isExploding, setIsExploding] = useState(false);
	const [claimedGiftData, setClaimedGiftData] = useState(null);
	const [processingTierClaims, setProcessingTierClaims] = useState(new Set());

	const apolloClient = useApolloClient();
	const snappyGiftService = new SnappyGiftService(apolloClient);
	
	const { rewardDetails, isModalLoaderVisible, handleOpenViewReward: handleOpenViewRewardGraphQL } = useRewardDetails(setIsViewRewardModalVisible, currentUser, apolloClient);
	
	useEffect(() => {
		const loadInitialData = async () => {
			setIsLoadingActivities(true);
			try {
				const [campaignsData, badgeAssignmentsData, snappyGiftsData, badgeGiftsWithStatusData] = await Promise.all([
					getCampaigns(),
					fetchSnappyGifts(),
					fetchBadgeGiftsWithStatus()
				]);
				
				const activities = snappyGiftService.processRecentActivities(
					badgeAssignmentsData || [],
					snappyGiftsData || [],
					badgeGiftsWithStatusData || []
				);
				setRecentActivities(activities);
				
				if (badgeAssignmentsData && badgeAssignmentsData.length > 0) {
					await checkForNewGiftsAndTriggerSpotlight(badgeAssignmentsData);
				}
			} finally {
				setIsLoadingActivities(false);
			}
		};
		
		loadInitialData();
	}, []);

	useEffect(() => {
		if (!isLoadingActivities && (badgeAssignments.length > 0 || snappyGifts.length > 0 || badgeGiftsWithStatus.length > 0)) {
			updateRecentActivities();
		}
	}, [badgeAssignments, snappyGifts, badgeGiftsWithStatus]);

	const getCampaigns = async () => {
		try {
			const response = await snappy({
				type: 'getCampaigns',
				userId: currentUser.id,
			});
			const campaignsData = response || [];
			setCampaigns(campaignsData);
			return campaignsData;
		} catch (error) {
			console.error('Error getCampaigns:', error);
			return [];
		}
	};

	const fetchSnappyGifts = async () => {
		try {
			const gifts = await snappyGiftService.querySnappyGiftsByUserIdCompanyId(currentUser.id, currentUser.company.id);
			setSnappyGifts(gifts);
			return gifts;
		} catch (error) {
			console.error('Error fetching snappy gifts:', error);
			return [];
		}
	};

	const fetchBadgeGiftsWithStatus = async () => {
		try {
			const gifts = await snappyGiftService.querySnappyGiftsByUserIdCompanyId(currentUser.id, currentUser.company.id);
			
			const badgeGifts = gifts.filter(gift =>
				gift.badgeAssignmentId &&
				gift.externalSnappyGiftId &&
				gift.externalSnappyGiftId !== 'undefined'
			);

			const giftsWithStatus = await Promise.all(
				badgeGifts.map(async (gift) => {
					const statusData = await snappyGiftService.getSnappyGift(currentUser.id, gift.externalSnappyGiftId);
					return {
						...gift,
						statusData
					};
				})
			);

			setBadgeGiftsWithStatus(giftsWithStatus);
			return giftsWithStatus;
		} catch (error) {
			console.error('Error fetching badge gifts with status:', error);
			return [];
		}
	};

	const updateRecentActivities = () => {
		const activities = snappyGiftService.processRecentActivities(badgeAssignments, snappyGifts, badgeGiftsWithStatus);
		setRecentActivities(activities);
	};

	const checkForNewGiftsAndTriggerSpotlight = async (badgeAssignmentsData) => {
		try {
			const giftData = await snappyGiftService.checkForNewGifts(
				currentUser.id,
				currentUser.company.id,
				badgeAssignmentsData || badgeAssignments
			);

			const { firstTimeGifts, subsequentGifts, allGifts } = giftData;

			if (firstTimeGifts.length > 0) {
				
				for (const gift of firstTimeGifts) {
					await snappyGiftService.markGiftAsSeen(
						currentUser.id,
						currentUser.company.id,
						gift.snappyGrantId
					);
				}
				
				const firstGift = firstTimeGifts[0];
				const mockRecognition = {
					id: `gift-spotlight-${firstGift.snappyGrantId}`,
					badgeId: `gift-badge-${firstGift.snappyGrantId}`,
					title: firstGift.badgeName,
					description: 'Gift Recognition',
					status: 'unviewed',
					createdAt: new Date().toISOString(),
					giftDetails: {
						link: 'https://snappy.com'
					},
					points: 0,
					iconCode: 'gift',
					badgeTypeCode: 'GIFT',
					color: '#E75DA7'
				};

				setTestRecognition(null);
				setDisableSpotlight(true);
				
				setTimeout(() => {
					setTestRecognition(mockRecognition);
					setDisableSpotlight(false);
				}, 100);

				const message = firstTimeGifts.length === 1 ?
					'You received a gift!' :
					`You received ${firstTimeGifts.length} gifts!`;
				
				const description = firstTimeGifts.length === 1 ?
					`Your "${firstGift.badgeName}" recognition includes a gift! Click the icon to find out more.` :
					`Your recognitions include ${firstTimeGifts.length} gifts! Click the icon to find out more.`;

				const key = `gift-notification-${Date.now()}`;
				notification.open({
					message,
					description,
					icon: <GiftOutlined style={{ color: '#E75DA7' }} />,
					duration: 0,
					key,
					placement: 'topRight',
					onClose: () => {
						setDisableSpotlight(true);
						setTestRecognition(null);
					},
					style: {
						borderLeft: '4px solid #E75DA7',
						boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
					}
				});

				await fetchSnappyGifts();
				await fetchBadgeGiftsWithStatus();
			} else if (subsequentGifts.length > 0) {
				setRecentActivities(prev =>
					prev.map((activity, index) => ({
						...activity,
						shouldShake: activity.type === 'gift_received',
						shakeIndex: index 
					}))
				);
			} else {
				setTestRecognition(null);
				setDisableSpotlight(true);
			}
		} catch (error) {
			console.error('Error checking for new gifts:', error);
		}
	};

	const handleTierClaim = async (tier) => {
		setProcessingTierClaims(prev => new Set([...prev, tier.id]));
		
		try {
			if (tier.gift && tier.gift.giftDetails && tier.gift.giftDetails.link) {
				window.open(tier.gift.giftDetails.link, '_blank');
				return;
			}

			const response = await snappy({
				type: 'createGifts',
				userId: currentUser.id,
				sendingMethod: 'link',
				recipients: [currentUser.id],
				campaignTierId: tier.id,
			});

			if (response && response.length > 0) {
				const result = response[0];
				if (result.giftId && result.giftDetails && result.giftDetails.link) {
					await getCampaigns();
					
					setClaimedGiftData({
						id: result.giftId,
						snappyUrl: result.giftDetails.link,
						title: 'Your Reward is Ready!',
						tierName: tier.tierName
					});
					
					setShowGiftModal(true);
					setIsExploding(true);
				} else {
					message.error(result.errorMessage || 'Error claiming gift.');
					console.error('Error claiming gift:', result.errorMessage);
				}
			} else {
				message.error('Unexpected response from the server.');
				console.error('Unexpected response:', response);
			}
		} catch (error) {
			console.error('Error claiming tier gift', error);
			message.error('An error occurred while claiming the gift.');
		} finally {
			setProcessingTierClaims(prev => {
				const newSet = new Set(prev);
				newSet.delete(tier.id);
				return newSet;
			});
		}
	};

	const handleClaimGift = async (activity) => {
		try {
			if (activity.type === 'tier_claim' && activity.tier) {
				return await handleTierClaim(activity.tier);
			}

			// Handle non-tier cards (badge-based gifts) with new logic
			if (activity.type === 'gift_received' && activity.giftStatus) {
				if (activity.giftStatus === 'unopened' && activity.link) {
					// For unopened gifts, just show confetti modal with the link
					setClaimedGiftData({
						id: activity.id,
						snappyUrl: activity.link,
						title: 'Your Reward is Ready!',
						badgeName: activity.badgeName
					});
					
					setShowGiftModal(true);
					setIsExploding(true);
					return;
				}
				
				if (activity.giftStatus === 'unwrapped' || activity.giftStatus === 'opened') {
					// For unwrapped gifts, the button should show "Finish Claiming Reward"
					// but we still need to handle the claim action
					if (activity.link) {
						window.open(activity.link, '_blank');
						return;
					}
				}
			}

			const snappyGrantId = activity.snappyGrantId;

			if (!snappyGrantId) {
				message.error('Unable to claim reward. Missing snappy grant ID.');
				return;
			}

			if (snappyGrantId.startsWith('test-')) {
				const mockClaimedGift = {
					id: activity.id,
					externalSnappyGiftId: 'test-snappy-123',
					createdAt: new Date().toISOString(),
					title: 'Test Gift Claimed'
				};
				
				setClaimedGiftData(mockClaimedGift);
				setShowGiftModal(true);
				setIsExploding(true);
				
				notification.destroy();
				
				setRecentActivities(prev => prev.filter(a => a.id !== activity.id));
				
				return;
			}

			// Find the badge assignment that matches this snappyGrantId
			const matchingBadgeAssignment = badgeAssignments.find(assignment =>
				assignment.badge?.snappyGrantId === snappyGrantId
			);

			if (!matchingBadgeAssignment) {
				message.error(`Unable to find badge assignment for snappyGrantId: ${snappyGrantId}`);
				return;
			}

			if (!matchingBadgeAssignment.badge?.snappyGrant?.snappyCampaignTierId) {
				message.error('Badge assignment missing snappyCampaignTierId information.');
				return;
			}

			const snappyCampaignTierId = matchingBadgeAssignment.badge.snappyGrant.snappyCampaignTierId;

			// Find the corresponding campaign tier using the snappyCampaignTierId
			
			let correspondingTier = null;
			
			// Search through all campaigns and their tiers
			for (const campaign of campaigns) {
				if (campaign.tiers) {
					const foundTier = campaign.tiers.find(tier => tier.id === snappyCampaignTierId);
					if (foundTier) {
						correspondingTier = foundTier;
						break;
					}
				}
			}
			
			if (!correspondingTier) {
				// Refresh campaigns and try again
				await getCampaigns();
				
				// Try again after refresh
				for (const campaign of campaigns) {
					if (campaign.tiers) {
						const foundTier = campaign.tiers.find(tier => tier.id === snappyCampaignTierId);
						if (foundTier) {
							correspondingTier = foundTier;
							break;
						}
					}
				}
				
				if (!correspondingTier) {
					message.error(`Unable to find campaign tier with ID: ${snappyCampaignTierId}`);
					return;
				}
			}

			await snappyGiftService.markGiftAsSeen(
				currentUser.id,
				currentUser.company.id,
				snappyGrantId
			);

			try {
				if (correspondingTier.gift === null) {
					const snappyGiftResponse = await snappy({
						type: 'createGifts',
						userId: currentUser.id,
						sendingMethod: 'link',
						recipients: [currentUser.id],
						campaignTierId: snappyCampaignTierId
					});

					if (snappyGiftResponse && Array.isArray(snappyGiftResponse) && snappyGiftResponse.length > 0) {
						const giftData = snappyGiftResponse[0];
						
						if (giftData.giftId && giftData.giftDetails?.link) {
							const snappyGiftInput = {
								userId: currentUser.id,
								companyId: currentUser.company.id,
								snappyGrantId: snappyGrantId,
								snappyCampaignTierId: snappyCampaignTierId,
								snappyCampaignId: correspondingTier.snappyCampaignId,
								externalSnappyGiftId: giftData.giftId,
								link: giftData.giftDetails.link,
								claimedOn: new Date().toISOString()
							};

							const createdSnappyGift = await snappyGiftService.createSnappyGiftRecord(snappyGiftInput);
							
							setClaimedGiftData({
								...createdSnappyGift,
								snappyUrl: giftData.giftDetails.link,
								title: 'Gift Created Successfully!'
							});
							
							setShowGiftModal(true);
							setIsExploding(true);
						} else {
							throw new Error('Gift data missing required fields (giftId or link)');
						}
					} else {
						throw new Error('Failed to create gift - invalid response format from Snappy API');
					}
				} else if (correspondingTier.gift && correspondingTier.gift.giftDetails?.status === 'unopened') {					
					const existingSnappyGifts = await snappyGiftService.querySnappyGiftsByUserIdCompanyId(currentUser.id, currentUser.company.id);
					const existingSnappyGift = existingSnappyGifts.find(gift =>
						gift.externalSnappyGiftId === correspondingTier.gift.giftId
					);
					
					let snappyGiftRecord;
					if (!existingSnappyGift) {
						const snappyGiftInput = {
							userId: currentUser.id,
							companyId: currentUser.company.id,
							snappyGrantId: snappyGrantId,
							snappyCampaignTierId: snappyCampaignTierId,
							snappyCampaignId: correspondingTier.snappyCampaignId,
							externalSnappyGiftId: correspondingTier.gift.giftId,
							link: correspondingTier.gift.giftDetails.link,
							claimedOn: new Date().toISOString()
						};
						
						snappyGiftRecord = await snappyGiftService.createSnappyGiftRecord(snappyGiftInput);
					} else {
						const updateInput = {
							id: existingSnappyGift.id,
							userId: currentUser.id,
							companyId: currentUser.company.id,
							snappyGrantId: snappyGrantId,
							snappyCampaignTierId: snappyCampaignTierId,
							snappyCampaignId: correspondingTier.snappyCampaignId,
							externalSnappyGiftId: correspondingTier.gift.giftId,
							link: correspondingTier.gift.giftDetails.link,
							claimedOn: existingSnappyGift.claimedOn || new Date().toISOString()
						};
						snappyGiftRecord = await snappyGiftService.updateSnappyGift(updateInput);
					}
					
					setClaimedGiftData({
						...snappyGiftRecord,
						snappyUrl: correspondingTier.gift.giftDetails.link,
						title: 'Gift Ready to Claim!'
					});
					
					setShowGiftModal(true);
					setIsExploding(true);
				} else {					
					const existingSnappyGifts = await snappyGiftService.querySnappyGiftsByUserIdCompanyId(currentUser.id, currentUser.company.id);
					const existingSnappyGift = existingSnappyGifts.find(gift =>
						gift.snappyGrantId === snappyGrantId
					);
					
					if (existingSnappyGift && correspondingTier.gift?.giftDetails?.link) {
						const updateInput = {
							id: existingSnappyGift.id,
							userId: currentUser.id,
							companyId: currentUser.company.id,
							snappyGrantId: snappyGrantId,
							snappyCampaignTierId: snappyCampaignTierId,
							snappyCampaignId: correspondingTier.snappyCampaignId,
							externalSnappyGiftId: correspondingTier.gift.giftId,
							link: correspondingTier.gift.giftDetails.link,
							claimedOn: existingSnappyGift.claimedOn || new Date().toISOString()
						};
						
						const updatedGift = await snappyGiftService.updateSnappyGift(updateInput);
						
						setClaimedGiftData({
							...updatedGift,
							snappyUrl: correspondingTier.gift.giftDetails.link,
							title: 'Gift Available!'
						});
						
						setShowGiftModal(true);
						setIsExploding(true);
					} else {
						message.error('Unable to process gift. Please contact support.');
					}
				}
			} catch (snappyError) {
				console.error('Snappy API failed:', snappyError);
				message.error('Failed to process gift. Please try again or contact support.');
			}

			await fetchSnappyGifts();
			await fetchBadgeGiftsWithStatus();
			await getCampaigns();

		} catch (error) {
			console.error('Error claiming gift:', error);
			message.error('Failed to process gift. Please try again.');
			
			await fetchSnappyGifts();
			await fetchBadgeGiftsWithStatus();
		}
	};

	const handleMarkAsSeen = async (activity) => {
		try {
			setRecentActivities(prev =>
				prev.map(a =>
					a.id === activity.id
						? { ...a, hasSeen: true, isNew: false }
						: a
				)
			);
		} catch (error) {
			console.error('Error marking activity as seen:', error);
		}
	};

	const handleViewRecognition = (activity) => {
		if (activity.rawData?.badgeId) {
			handleOpenViewReward(activity.rawData.badgeId);
		}
	};

	const handleGiftModalClose = () => {
		setShowGiftModal(false);
		setIsExploding(false);
		setClaimedGiftData(null);
	};

	const handleGiftRedeem = () => {
		if (claimedGiftData?.snappyUrl) {
			window.open(claimedGiftData.snappyUrl, '_blank');
		} else if (claimedGiftData?.externalSnappyGiftId) {
			const snappyUrl = `https://snappy.com/gift/${claimedGiftData.externalSnappyGiftId}`;
			window.open(snappyUrl, '_blank');
		} else {
			window.open('https://snappy.com', '_blank');
		}
		handleGiftModalClose();
	};

	const handleViewBadgeDetails = (badgeAssignment) => {
		const badgeId = badgeAssignment.badgeId || badgeAssignment?.badge?.id;
		if (badgeId) {
			handleOpenViewRewardGraphQL(badgeId, badgeAssignment.completedAt, badgeAssignment);
		}
	};
	const handleCloseViewReward = () => {
		setIsViewRewardModalVisible(false);
	};
	

	const createTestRecognition = () => {
		setDisableSpotlight(false);
		
		const testId = `test-${Date.now()}`;
		
		const newTestRecognition = {
			id: testId,
			badgeId: `test-badge-${Date.now()}`,
			title: 'Test Recognition',
			description: 'This is a test recognition to verify animations',
			status: 'unviewed',
			createdAt: new Date().toISOString(),
			giftDetails: {
				link: 'https://snappy.com'
			},
			points: 500,
			iconCode: 'trophy',
			badgeTypeCode: 'PEERTOPEER',
			color: '#E75DA7'
		};
		
		const testGiftActivity = {
			id: `test-gift-${Date.now()}`,
			type: 'gift_received',
			title: 'Test Gift Received',
			description: 'You received a test gift!',
			createdAt: new Date().toISOString(),
			isNew: true,
			shouldShake: true,
			shakeIndex: 0,
			snappyGrantId: 'test-snappy-grant-123',
			rawData: {
				campaignTierId: 'test-campaign-tier-456'
			}
		};
		
		setRecentActivities(prev => [testGiftActivity, ...prev]);
		
		const recognitionId = newTestRecognition.id;
		setTestRecognition(newTestRecognition);
		
		const key = `notification-${recognitionId}`;
		notification.open({
			message: 'You received a recognition!',
			description: 'Your Recognition included a gift! Click the icon to find out more.',
			icon: <TrophyOutlined style={{ color: '#E75DA7' }} />,
			duration: 0,
			key,
			placement: 'topRight',
			onClose: () => {
				setDisableSpotlight(true);
			},
			style: {
				borderLeft: '4px solid #E75DA7',
				boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
			}
		});
		
		message.success('Test recognition created! Scroll down to see it with spotlight effect.');
	};

	const handleClearTestRecognition = () => {
		setTestRecognition(null);
		setDisableSpotlight(true);
	};


	return (
		<>
			{currentUser?.featureFlags?.includes('test_snappy_spotlight') ? (
				<div style={{ marginBottom: '20px', textAlign: 'center' }}>
					<Button
						type="primary"
						onClick={createTestRecognition}
						size="large"
						style={{
							background: '#E75DA7',
							borderColor: '#E75DA7',
							boxShadow: '0 4px 12px rgba(231, 93, 167, 0.3)'
						}}
					>
						Create Test Gift with Button Spotlight
					</Button>
				</div>
			) : null}
			<MyrewardsPresentation
				tiers={
					campaigns.length > 0 && campaigns[0]?.tiers ? campaigns[0].tiers : []
				}
				handleOpenViewReward={handleOpenViewRewardGraphQL}
				handleCloseViewReward={handleCloseViewReward}
				isViewRewardModalVisible={isViewRewardModalVisible}
				rewardDetails={rewardDetails}
				isModalLoaderVisible={isModalLoaderVisible}
				currentUser={currentUser}
				recentActivities={recentActivities}
				badgeAssignments={badgeAssignments}
				onClaimGift={handleClaimGift}
				testRecognition={testRecognition}
				disableSpotlight={disableSpotlight}
				onViewRecognition={handleViewRecognition}
				onViewBadgeDetails={handleViewBadgeDetails}
				onMarkAsSeen={handleMarkAsSeen}
				onClearTestRecognition={handleClearTestRecognition}
				processingTierClaims={processingTierClaims}
				isLoadingActivities={isLoadingActivities}
			/>

			{/* Gift Claiming Confetti Modal */}
			<Modal
				title="Congratulations!"
				open={showGiftModal}
				onCancel={handleGiftModalClose}
				footer={[
					<Button key="close" onClick={handleGiftModalClose}>
						Close
					</Button>,
					<Button key="redeem" type="primary" onClick={handleGiftRedeem} size="large">
						Go to Snappy and unwrap it!
					</Button>,
				]}
				className="recognition-reward-modal"
				width={800}
				centered
			>
				{isExploding && (
					<div style={{position: 'absolute', overflow: 'visible', top: '25%', left:'50%'}}>
						<ConfettiExplosion {...confettiConfig} />
					</div>
				)}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.2 }}
					className="recognition-modal-content"
				>
					<motion.h2
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{
							duration: 0.8,
							delay: 0.4,
							type: "spring",
							stiffness: 100
						}}
						style={{marginTop: 12}}
					>
						Your Gift has been claimed successfully!
					</motion.h2>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.5, delay: 0.6 }}
					>
						<img src={GiftImage} alt="Art of wrapped gift" />
					</motion.div>
				</motion.div>
			</Modal>
		</>
	);
};

export default MyrewardsContainer;
